import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "@/i18n/router";
import { useQuery } from "@tanstack/react-query";
import { Search as SearchIcon, ArrowRight } from "lucide-react";
import { Seo } from "@/lib/seo";
import { PageHero } from "@/components/layout/PageHero";
import { Reveal } from "@/components/motion/Reveal";
import { useLang } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";

// Static page index for site-wide navigation results
const PAGES = [
  { title: "AI Governance OS", excerpt: "Enterprise AI Governance Operating System.", url: "/ai-governance-os", label: "Platform" },
  { title: "Technology", excerpt: "Verifiable AI governance architecture.", url: "/technology", label: "Page" },
  { title: "Solutions", excerpt: "Governance for critical sectors.", url: "/solutions", label: "Page" },
  { title: "Trust Center", excerpt: "Security, privacy and responsible AI.", url: "/trust-center", label: "Page" },
  { title: "European Regulation", excerpt: "EU AI Act, GDPR, NIS2 and standards.", url: "/regulation", label: "Page" },
  { title: "Knowledge Center", excerpt: "Glossary, FAQ and governance guides.", url: "/knowledge", label: "Page" },
  { title: "Documentation", excerpt: "Technical documentation and API references.", url: "/documentation", label: "Page" },
  { title: "Publications", excerpt: "White papers and institutional publications.", url: "/publications", label: "Page" },
  { title: "Comparison", excerpt: "Why AstroLife vs generic solutions.", url: "/comparison", label: "Page" },
  { title: "Enterprise", excerpt: "Demo, partnership and support.", url: "/enterprise", label: "Page" },
];

export default function Search() {
  const { tr } = useLang();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [term, setTerm] = useState(q);

  useEffect(() => setTerm(q), [q]);

  const { data, isFetching } = useQuery({
    queryKey: ["search", q],
    queryFn: async () => (await api.get(`/search?q=${encodeURIComponent(q)}`)).data,
    enabled: q.length >= 2,
  });

  const submit = (e) => {
    e.preventDefault();
    setParams(term ? { q: term } : {});
  };

  const pageMatches = q.length >= 2 ? PAGES.filter((p) => (p.title + p.excerpt).toLowerCase().includes(q.toLowerCase())) : [];
  const backend = data?.results || [];
  const all = [...pageMatches.map((p) => ({ ...p, type: "Page" })), ...backend];

  return (
    <>
      <Seo title={tr("Search")} description={tr("Search AstroLife pages, blog, publications and resources.")} path="/search" />
      <PageHero overline={tr("Search")} title={tr("Search the knowledge platform.")} breadcrumbs={[{ label: tr("Search") }]}>
        <form onSubmit={submit} className="mt-8 flex max-w-2xl gap-2">
          <div className="relative flex-1">
            <SearchIcon className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-silver/40" />
            <input autoFocus value={term} onChange={(e) => setTerm(e.target.value)} placeholder={tr("Search pages, blog, publications…")} data-testid="global-search-input"
              className="w-full rounded-full border border-navy-border bg-navy-surface py-4 pl-12 pr-4 text-white placeholder:text-silver/40 focus:border-astro-blue focus:outline-none" />
          </div>
          <button type="submit" data-testid="global-search-submit" className="rounded-full bg-astro-blue px-7 py-4 font-semibold text-white hover:bg-astro-light transition-colors">{tr("Search")}</button>
        </form>
      </PageHero>

      <section className="bg-navy py-16 lg:py-24 min-h-[40vh]">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          {q.length < 2 ? (
            <p className="text-center text-silver/50">{tr("Enter at least 2 characters to search.")}</p>
          ) : isFetching ? (
            <p className="text-center text-silver/50">{tr("Searching…")}</p>
          ) : all.length === 0 ? (
            <p className="text-center text-silver/50" data-testid="search-empty">{tr("No results for")} “{q}”.</p>
          ) : (
            <div className="space-y-3" data-testid="search-results">
              <p className="mb-4 text-sm text-silver/50">{all.length} {all.length !== 1 ? tr("results") : tr("result")} {tr("for")} “{q}”</p>
              {all.map((r, i) => (
                <Reveal key={i} delay={i * 0.02}>
                  <Link to={r.url} className="group flex items-center justify-between gap-4 rounded-xl border border-navy-border bg-navy-surface p-5 hover:border-astro-blue/40 transition-colors">
                    <div>
                      <span className="text-xs font-semibold uppercase tracking-wider text-astro-light">{tr(r.label || r.type)}</span>
                      <p className="mt-1 font-heading font-semibold text-white group-hover:text-astro-light transition-colors">{r.type === "Page" ? tr(r.title) : r.title}</p>
                      {r.excerpt && <p className="mt-1 line-clamp-1 text-sm text-silver/60">{r.type === "Page" ? tr(r.excerpt) : r.excerpt}</p>}
                    </div>
                    <ArrowRight className="h-4 w-4 flex-shrink-0 text-silver/40" />
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
